import { IconCrossLarge } from 'central-icons/IconCrossLarge';
import { IconPlusLarge } from 'central-icons/IconPlusLarge';
import classNames from 'classnames';

import type { SelectionCategories } from '@revelio/iso-utility';
import { Button, Text } from '@revelio/ui';

import {
  GEOGRAPHY_GRANULARITY_FILTERS,
  ROLE_GRANULARITY_FILTERS,
} from '../../constants/filter-constants';
import {
  type Filter,
  FilterType,
  type TreeSelection,
} from '../../types/filters';
import styles from './SegmentForm.module.css';
import layoutStyles from './segment-form.module.css';

interface SegmentFormProps {
  segmentations: Record<string, Filter> | undefined;
  onEditSegment: (segmentKey: string) => () => void;
  onRemoveSegment: (
    segmentKey: string
  ) => (e: React.MouseEvent<SVGElement, MouseEvent>) => void;
  onNewSegment: () => void;
}

const getSegmentGroup = (levelId: string): string => {
  if (ROLE_GRANULARITY_FILTERS.includes(levelId as SelectionCategories)) {
    return 'Role_Granularity';
  }

  if (GEOGRAPHY_GRANULARITY_FILTERS.includes(levelId as SelectionCategories)) {
    return 'Geography_Granularity';
  }

  return levelId;
};

const getDisplayNames = (
  selections: TreeSelection[] | Array<{ id: string; name: string }>
): string => {
  const names = selections.map((s) => s.name).filter(Boolean);

  return names.length > 2
    ? [
        ...names.slice(0, 2),
        `and ${names.length - 2} other${names.length - 2 > 1 ? 's' : ''}`,
      ].join(', ')
    : names.join(', ');
};

const getSegmentLevelId = (filter: Filter): string | null => {
  if (filter.type === FilterType.TREE && filter.selections.length > 0) {
    return filter.selections[0]?.levelId ?? null;
  }
  if (filter.type === FilterType.LIST && filter.selections.length > 0) {
    // For list filters, use the selectionListId as the levelId
    return filter.selectionListId ?? null;
  }
  return null;
};

export const SegmentForm = ({
  segmentations,
  onEditSegment,
  onRemoveSegment,
  onNewSegment,
}: SegmentFormProps) => {
  const hasSegments = segmentations && Object.keys(segmentations).length > 0;

  // Group segments by their levelId
  const groupedSegments = segmentations
    ? Object.entries(segmentations).reduce<
        Record<string, Array<[string, Filter]>>
      >((groups, [key, filter]) => {
        const levelId = getSegmentLevelId(filter);
        if (!levelId) return groups;

        const group = getSegmentGroup(levelId);
        if (!groups[group]) {
          groups[group] = [];
        }
        groups[group].push([key, filter]);
        return groups;
      }, {})
    : {};

  return (
    <>
      {hasSegments && (
        <div className={layoutStyles.segmentGroups}>
          {Object.entries(groupedSegments).map(([groupKey, segments]) => {
            const firstSegment = segments[0];
            if (!firstSegment) return null;

            const [segmentKey, filter] = firstSegment;
            let displayNames = '';

            if (
              filter.type === FilterType.TREE ||
              filter.type === FilterType.LIST
            ) {
              displayNames = getDisplayNames(filter.selections);
            }

            return (
              <div className={layoutStyles.groupRow} key={groupKey}>
                <Text fontSize="sm">
                  {GEOGRAPHY_GRANULARITY_FILTERS.includes(
                    groupKey as SelectionCategories
                  )
                    ? 'in'
                    : 'for'}
                </Text>
                <div
                  onClick={onEditSegment(segmentKey)}
                  className={classNames(
                    styles.segmentChip,
                    layoutStyles.segmentChipLayout
                  )}
                >
                  <Text fontSize="sm" className={styles.segmentChipLabel}>
                    {displayNames}
                  </Text>
                  <IconCrossLarge
                    size="1em"
                    onClick={onRemoveSegment(segmentKey)}
                    className={styles.segmentChipRemove}
                    data-testid={`remove-segment-${segmentKey}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
      <Button
        onClick={onNewSegment}
        size="sm"
        className={styles.newSegmentButton}
        style={{
          marginBottom: '0.625rem',
          marginLeft: hasSegments ? '0.75rem' : 0,
        }}
      >
        <IconPlusLarge size="0.75rem" className={layoutStyles.addSegmentIcon} />
        <Text as="span" fontSize="sm">
          segment by employee type
        </Text>
      </Button>
    </>
  );
};
